import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Eye, ShieldCheck, Sparkles, Globe2 } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div data-testid="about-page" className="surface-obsidian text-platinum">
      <SEO
        title="About GIWAHS — The Modern Sourcing Intelligence Platform"
        description="GIWAHS is the editorial intelligence layer for global fashion sourcing — connecting retailers, brands, and entrepreneurs with verified factories across 12+ countries."
      />
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-28">
          <span className="label-overline">/ About Giwahs</span>
          <h1 className="mt-6 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 8vw, 7rem)', lineHeight: 0.92 }}>
            Sourcing,<br />reimagined as<br /><em className="giwahs-gold-emphasis" style={{ fontStyle: 'italic' }}>intelligence.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-700 md:text-lg">
            Giwahs is the editorial intelligence layer between fashion operators and the factories who make their products real. We help retailers, brands, and entrepreneurs find trends, identify manufacturers, and connect globally — without the noise of generic B2B marketplaces.
          </p>
        </div>
      </section>

      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] grid gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-5">
            <span className="label-overline">/ 01 — Mission</span>
            <h2 className="mt-4 font-display text-4xl tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>What we believe.</h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-lg leading-relaxed text-neutral-800">
              Finding the right manufacturing partner isn't always straightforward — discoverability is fractured, verification is inconsistent, and brand-to-factory relationships sit hidden behind layers of agents and trading companies. GIWAHS does the hard work of organising and assessing supplier information, so you can focus on building your business with confidence.
            </p>
            <p className="mt-5 text-lg leading-relaxed text-neutral-800">
              We don't sell goods. We surface the makers — with verification, classification, and brand-intelligence context that you can act on.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 02 — Principles</span>
          <h2 className="mt-4 font-display text-4xl tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>How we operate.</h2>
          <div className="mt-12 grid gap-px bg-black md:grid-cols-2 lg:grid-cols-4">
            {[
              { i: Eye, t: 'Transparency by default', d: 'Brand associations are categorised as Verified, Publicly Reported, Unverified, or Historical — never blurred.' },
              { i: ShieldCheck, t: 'Verification, not vanity', d: 'Four-tier badge system grounded in registration, audit, and documentation — not paid placements.' },
              { i: Sparkles, t: 'Editorial intelligence', d: 'Trends, intelligence briefs, and supplier intel curated like a fashion publication — fast, sharp, useful.' },
              { i: Globe2, t: 'Global by design', d: '12+ sourcing countries indexed — Asia, Europe, Latin America. Built to scale to 50+.' },
            ].map((b, i) => (
              <div key={i} className="bg-white p-7">
                <b.i size={22} strokeWidth={1.5} />
                <div className="mt-6 font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>{b.t}</div>
                <p className="mt-3 text-sm leading-relaxed text-neutral-600">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-black bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline text-neutral-400">/ 03 — Roadmap</span>
          <h2 className="mt-4 font-display text-4xl tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>What's next.</h2>
          <div className="mt-12 space-y-px bg-neutral-800">
            {[
              { stage: 'Q1 26', title: 'MVP Launch', desc: 'Directory, RFQ builder, supplier profiles, AI image search UI.' },
              { stage: 'Q2 26', title: 'AI Vision Integration', desc: 'Connect OpenAI Vision API for live product photo → factory matching.' },
              { stage: 'Q3 26', title: 'Verification Engine', desc: 'Trust score computed from real signals: registration, audit, response data.' },
              { stage: 'Q4 26', title: 'Sourcing Assistant', desc: 'Conversational AI for buyer guidance, sourcing risk assessment, and supplier intel.' },
            ].map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-4 bg-[#0A0A0A] px-5 py-6 md:px-8 md:py-8">
                <div className="col-span-3 md:col-span-2 font-mono-data text-sm text-neutral-400">{r.stage}</div>
                <div className="col-span-9 md:col-span-3 font-display text-xl tracking-tight md:text-2xl" style={{ fontWeight: 600 }}>{r.title}</div>
                <div className="col-span-12 md:col-span-7 text-sm leading-relaxed text-neutral-400">{r.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="grid items-end gap-10 md:grid-cols-2">
            <h2 className="font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 6vw, 5rem)', lineHeight: 0.95 }}>
              Want to be part of it?
            </h2>
            <div className="flex flex-col gap-4 md:items-end">
              <p className="max-w-md text-neutral-700">Whether you're a brand, retailer, or manufacturer — get in touch and join the platform.</p>
              <Link to="/contact" data-testid="about-contact-cta" className="btn-giwahs btn-giwahs-primary inline-flex w-fit items-center gap-2">
                Contact the team <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
